import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ChevronDown, ChevronRight, ClipboardType, FileUp, Loader2, Sparkles } from 'lucide-react';
import { FileDropzone } from '../FileDropzone';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { BreathingDot } from '../ui/breathing';
import { InputModeSwitch } from '../ui/input-mode-switch';
import { Textarea } from '../ui/input';
import { api, type KbRun, type RoutedIngestResult } from '../../lib/api';
import { formatRunLabel, formatSource, useSourceLabels } from '../../lib/source-label';
import { cn } from '../../lib/utils';

const TERMINAL: KbRun['status'][] = ['COMPLETED', 'FAILED'];
const POLL_MS = 2_500;

/** Each file is its own agentic run of about a minute, and they cannot be parallelised. */
const LARGE_BATCH = 10;

/**
 * Putting the first content into a project, from the project's own page.
 *
 * A new project is a dead end until something is in it: with no pages it has no description, and
 * with no description the router will not send it anything — so it cannot fill itself. The way out
 * is filing something directly, and that path existed but was somewhere else (the knowledge base's
 * side rail, or the org-level Add page where you then have to pick this project back out of a list).
 * Landing on an empty project and being handed a box is the shape that matches what someone is
 * actually trying to do.
 *
 * **Routed by default; pinning is a choice.** It used to be pinned unconditionally, on the reasoning
 * that choosing a project by being on its page settles the destination. That reasoning was wrong in
 * the common case: knowledge dropped on a project is often *shared* with the projects above or below
 * it, and pinning quietly denied it to them — a partner call filed on one engagement never reaching
 * the parent it also concerns. So the default is the agent's judgement, exactly as on `/add`, and the
 * toggle is for when you know better than it does.
 *
 * The exception is an **empty** project, which defaults to pinned: placement reads a project's own
 * pages and description, and a project that has neither cannot be chosen — so routing the very
 * content meant to bootstrap it would file it somewhere that already looks more relevant, and the
 * empty project would stay empty. Being unable to fill itself is precisely the dead end this card
 * exists to open.
 */
export function ProjectStarter({
  projectId,
  projectName,
  isEmpty,
  startOpen,
  failedUploads,
  incomingFiles,
  onIngested,
}: {
  projectId: string;
  projectName: string;
  /** Nothing in the knowledge base yet — the case this exists for, so it opens itself. */
  isEmpty: boolean;
  /** Arrived here meaning to add something — set by the create flow. */
  startOpen?: boolean;
  /** Files the create dialog could not upload, named so they can be dropped again rather than lost. */
  failedUploads?: string[];
  /** Files dropped anywhere on the page, handed over to be queued here. */
  incomingFiles?: File[];
  onIngested: () => void;
}) {
  const [open, setOpen] = useState(isEmpty || startOpen === true);
  const [content, setContent] = useState('');
  /**
   * Whether the paste box has been asked for.
   *
   * Files lead here for the same reason they lead on `/add`: what people have to hand is documents,
   * decks and exported notes, and pasting is the fallback for when the thing is in a window instead.
   * Showing both at once gave them equal weight and made the card twice as tall as the act needs.
   */
  const [pasting, setPasting] = useState(false);
  /**
   * Whether to keep this in one project rather than letting the run place it.
   *
   * Defaults on only for an empty project — see the class comment for why routing cannot bootstrap
   * one. Component state rather than a stored preference: the right answer is a property of the
   * material being dropped, not of the person dropping it.
   */
  const [pinned, setPinned] = useState(isEmpty);
  /**
   * The same value, readable without making `queueFiles` depend on it.
   *
   * Putting `pinned` in that callback's dependencies re-creates it whenever the toggle moves, and the
   * page-drop effect below keys on the callback — so flipping the toggle after dropping files on the
   * page would run the effect again and ingest the same files a second time. The ref keeps the
   * callback stable while still reading the current choice at the moment of the call.
   */
  const pinnedRef = useRef(pinned);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);
  const [busy, setBusy] = useState(false);
  const [tracked, setTracked] = useState<KbRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // An empty project that has just received its first run should stay open — collapsing under
  // someone mid-task because `isEmpty` flipped would hide the queue they are watching.
  //
  // Opening is all this does now. It used to also put the cursor in the paste box, which no longer
  // makes sense: arriving here meaning to add something means the dropzone, and that is what the
  // card opens on. Focus follows the deliberate switch to pasting instead.
  useEffect(() => {
    if (isEmpty || startOpen) setOpen(true);
  }, [isEmpty, startOpen]);

  /**
   * Adopts runs already in flight when this opens from the create flow.
   *
   * Files dropped into the create dialog are queued before the project page exists, so their runs
   * belong to nobody by the time it does. Picking up whatever is still moving is what makes the
   * hand-off seamless — otherwise you create a project with five documents and land on a page that
   * looks like nothing happened, while five runs churn away invisibly.
   *
   * Only non-terminal runs, and only once: adopting finished ones would replay a history that the
   * Activity section below already shows.
   */
  const adopted = useRef(false);
  useEffect(() => {
    if (!startOpen || adopted.current) return;
    adopted.current = true;
    api.kb
      .runs(projectId, { limit: 20 })
      .then((runs) => {
        const live = runs.filter((run) => !TERMINAL.includes(run.status)).reverse();
        if (live.length > 0) setTracked((prev) => (prev.length > 0 ? prev : live));
      })
      .catch(() => undefined);
  }, [startOpen, projectId]);

  const pending = tracked.filter((run) => !TERMINAL.includes(run.status));

  const queueFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setError(null);
      setNotice(
        files.length > LARGE_BATCH
          ? `Queueing ${files.length} files. They are absorbed one at a time — roughly a minute each — so this will take a while.`
          : null,
      );
      setBusy(true);
      try {
        // Sequentially, so the runs enter the queue in the order they were dropped. The worker takes
        // one project's runs one at a time regardless, so this is what makes the on-screen order
        // match the order they are actually absorbed in.
        for (const file of files) {
          try {
            const run = pinnedRef.current
              ? await api.kb.ingestFile(projectId, file, { source: file.name })
              : placeholderFor(
                  await api.kbIngest.file(file, { source: file.name }),
                  file.name,
                );
            if (run) setTracked((prev) => [...prev, run]);
          } catch (e) {
            setError(`${file.name}: ${e instanceof Error ? e.message : String(e)}`);
          }
        }
      } finally {
        setBusy(false);
      }
    },
    [projectId],
  );

  // Files dropped on the page rather than on the box. Keyed on the array identity the parent hands
  // over, which it replaces per drop.
  useEffect(() => {
    if (incomingFiles && incomingFiles.length > 0) {
      setOpen(true);
      void queueFiles(incomingFiles);
    }
  }, [incomingFiles, queueFiles]);

  const submitText = async () => {
    const body = content.trim();
    if (body.length < 10) return;
    setBusy(true);
    setError(null);
    try {
      // No source: the field that used to supply one is gone, and the server's default ("manual")
      // says the true thing — someone typed this in — where a blank box mostly collected nothing.
      const run = pinned
        ? await api.kb.ingest(projectId, { content: body })
        : placeholderFor(await api.kbIngest.content({ content: body }), null);
      setContent('');
      if (run) setTracked((prev) => [...prev, run]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Polls the project's runs and re-matches by id, rather than one request per tracked run.
   *
   * Stops the moment nothing is moving — an idle project should not be issuing a request every two
   * seconds — and tells the page when something finishes, since that is when its stats, its tree and
   * (first time round) its description have all changed.
   */
  useEffect(() => {
    if (pending.length === 0) return;
    const timer = setInterval(async () => {
      try {
        const fresh = await api.kb.runs(projectId, { limit: 40 });
        const byId = new Map(fresh.map((run) => [run.id, run]));
        setTracked((prev) => {
          const next = prev.map((run) => byId.get(run.id) ?? run);
          const settled = next.some((run, i) => TERMINAL.includes(run.status) && !TERMINAL.includes(prev[i].status));
          if (settled) onIngested();
          return next;
        });
      } catch {
        /* keep polling — a transient failure should not abandon the queue */
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [pending.length, projectId, onIngested]);

  const done = tracked.length - pending.length;

  return (
    <Card className={cn('overflow-hidden', isEmpty && 'border-orange-300 dark:border-orange-900')}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-surface-subtle transition-colors"
      >
        {open ? (
          <ChevronDown size={13} className="text-ink-faint flex-shrink-0" />
        ) : (
          <ChevronRight size={13} className="text-ink-faint flex-shrink-0" />
        )}
        <Sparkles size={13} className="text-orange-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-ink">
          {/* Not "Add to this project" any more: the default lets the run place the content, so a
              heading promising this project would be the first thing on the card to be untrue. */}
          {isEmpty ? `Give ${projectName} something to work with` : 'Add to the knowledge base'}
        </span>
        {pending.length > 0 && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-orange-600 dark:text-orange-400">
            <BreathingDot size={7} />
            {pending.length} absorbing
          </span>
        )}
        {tracked.length > 0 && pending.length === 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-success-text">
            <Check size={11} />
            {done} done
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border-subtle">
          {isEmpty && (
            <p className="text-xs text-ink-muted leading-relaxed">
              Drop a few documents or paste some notes. The agent reads them and writes this project's
              first pages — and once it has pages, it describes itself, which is what lets content
              arriving from Slack or Granola be filed here automatically.
            </p>
          )}

          {pasting ? (
            <div className="space-y-2">
              {/* The button sits *inside* the box, bottom-right.
                  
                  It used to be a row beneath, sharing that row with a "Where it came from" field —
                  so the one control that does anything was the smaller half of a line about
                  metadata. Floating it in the corner of the text puts the action where the text
                  being acted on is, and gives the box the full width back.
                  
                  `pb-14` is what keeps that honest: without it a long paste scrolls its last line
                  under the button. */}
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste notes, a thread, a brief — anything this project should know."
                  className="text-xs h-72 pb-14 resize-y"
                />
                {/* On an opaque plate: `pb-14` above keeps the last *line* clear, but a scrolled
                    textarea still draws its content through the padding, so the cluster needs a
                    background of its own rather than relying on empty space being empty. */}
                <div className="absolute bottom-3 right-3 flex items-center gap-2 bg-card rounded-lg pl-2">
                  <RoutingSwitch pinned={pinned} onChange={setPinned} projectName={projectName} />
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busy || content.trim().length < 10}
                    onClick={submitText}
                    className="shadow-sm"
                  >
                    {busy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Sparkles size={12} className="mr-1.5" />}
                    Add
                  </Button>
                </div>
              </div>
              <InputModeSwitch
                icon={FileUp}
                label="Upload files instead"
                onClick={() => setPasting(false)}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <FileDropzone
                onFilesAdded={queueFiles}
                disabled={busy}
                label="Drop files here, or browse"
                helperText="PDF, Word, Markdown or plain text. Several at once is fine."
              />
              {/* Below the dropzone, not level with it: the primary act is dropping onto the area
                  above, and an alternative placed alongside competes for the same glance.
                  
                  Routing keeps the right-hand side in both modes. A drop submits itself, so there is
                  no Add button here to sit beside — holding the same corner is what stops the control
                  from appearing to move when you switch. */}
              <div className="flex items-center justify-between gap-3">
                <InputModeSwitch
                  icon={ClipboardType}
                  label="Or paste content instead"
                  onClick={() => {
                    setPasting(true);
                    // One frame, so the box exists before we reach for it.
                    requestAnimationFrame(() => textareaRef.current?.focus());
                  }}
                />
                <RoutingSwitch pinned={pinned} onChange={setPinned} projectName={projectName} />
              </div>
            </div>
          )}

          {/* Carried over from the create dialog: the project was made, these files were not taken.
              Named rather than counted, so they can be found and dropped again. */}
          {failedUploads && failedUploads.length > 0 && (
            <p className="text-[11px] text-warning-text bg-warning-bg border border-warning-ring rounded px-2 py-1.5 leading-snug">
              The project was created, but {failedUploads.length === 1 ? 'this file' : 'these files'} could
              not be uploaded: {failedUploads.join(', ')}. Drop {failedUploads.length === 1 ? 'it' : 'them'} again here.
            </p>
          )}

          {error && (
            <p className="text-[11px] text-danger-text bg-danger-bg border border-danger-ring rounded px-2 py-1.5">
              {error}
            </p>
          )}
          {notice && (
            <p className="text-[11px] text-warning-text bg-warning-bg border border-warning-ring rounded px-2 py-1.5">
              {notice}
            </p>
          )}

          {tracked.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">
                {pending.length > 0 ? `Absorbing — ${done} of ${tracked.length} done` : 'Absorbed'}
              </p>
              {tracked.map((run) => (
                <RunLine key={run.id} run={run} />
              ))}
              {pending.length > 0 && (
                <p className="text-[10px] text-ink-faint">
                  Each file is its own run of about a minute, taken one at a time. Keep browsing — this
                  keeps reporting.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Where a drop goes, as a control beside the action rather than a paragraph above it.
 *
 * Two buttons rather than a switch: a switch has an implied "off", and neither of these is off — they
 * are two legitimate answers. It was a wider segmented control with a sentence underneath explaining
 * the consequence, which is the thing people get wrong: pinning does not merely *choose* this project,
 * it **withholds** the content from the projects above and below. That sentence now lives in the
 * `title` of each option, which keeps it available without spending three lines of the card on it
 * every time someone opens the box.
 */
function RoutingSwitch({
  pinned,
  onChange,
  projectName,
}: {
  pinned: boolean;
  onChange: (pinned: boolean) => void;
  projectName: string;
}) {
  // Default first, matching `/add`, where letting the agent decide is also the leading option.
  const options = [
    {
      value: false,
      label: 'Auto',
      title: `Read against the whole hierarchy and filed wherever it belongs — which may be ${projectName}, a project above or below it, or several at once.`,
    },
    {
      value: true,
      label: 'Keep here',
      title: `Filed into ${projectName} and nowhere else. The agent still chooses which page, but it will not reach the projects above or below — even if the content also concerns them.`,
    },
  ];

  return (
    <span className="inline-flex items-center gap-1.5 flex-shrink-0">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Routing</span>
      <span className="inline-flex rounded-md border border-border bg-surface-subtle p-0.5">
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
              pinned === option.value
                ? 'bg-card text-ink shadow-sm'
                : 'text-ink-faint hover:text-ink-secondary',
            )}
          >
            {option.label}
          </button>
        ))}
      </span>
    </span>
  );
}

/**
 * The routed endpoints answer with a thin `RoutedRun`; this card tracks full `KbRun`s.
 *
 * Filled out rather than cast, and every value here is true of a run that has only just been queued:
 * nothing has been read, so every counter is zero. The poll below replaces it with the real row
 * within a couple of seconds — this exists so the line appears the instant you press the button
 * instead of after the first poll.
 *
 * Returns null for a duplicate, which produced no run to follow: the response says so and there is
 * nothing to report but the absence of work.
 */
function placeholderFor(result: RoutedIngestResult, fileName: string | null): KbRun | null {
  const routed = result.runs[0];
  if (!routed) return null;

  return {
    id: routed.id,
    // The organization root for a routed run — the scope it reads from, not where it will write.
    projectId: routed.projectId,
    requestedById: '',
    source: routed.source,
    sourceType: 'MANUAL',
    feedItemId: null,
    fileName: fileName ?? routed.fileName,
    status: routed.status,
    chunks: 0,
    nodesCreated: 0,
    nodesUpdated: 0,
    nodesDeleted: 0,
    nodesMerged: 0,
    nodesMoved: 0,
    flaggedForReview: 0,
    triage: true,
    destinations: null,
    result: null,
    toolCalls: 0,
    // Nothing has been spent yet, which is true of a run that has only just been queued.
    costUsd: 0,
    inputTokens: 0,
    outputTokens: 0,
    cachedTokens: 0,
    reasoningTokens: 0,
    modelCalls: 0,
    phase: null,
    activity: 'deciding where this belongs…',
    errorMessage: null,
    queuedAt: new Date().toISOString(),
    startedAt: null,
    finishedAt: null,
  };
}

function RunLine({ run }: { run: KbRun }) {
  const live = !TERMINAL.includes(run.status);
  const sourceLabels = useSourceLabels();

  return (
    <div className="flex items-center gap-2 text-[11px]">
      {live ? (
        <BreathingDot size={8} className="flex-shrink-0" />
      ) : run.status === 'FAILED' ? (
        <AlertTriangle size={11} className="text-red-500 flex-shrink-0" />
      ) : (
        <Check size={11} className="text-green-500 flex-shrink-0" />
      )}
      <span className="text-ink-secondary truncate max-w-[16rem]">
        {formatRunLabel(run.fileName, sourceLabels) || formatSource(run.source, sourceLabels)}
      </span>
      <span className="text-ink-faint truncate">
        {live
          ? run.activity ?? (run.status === 'QUEUED' ? 'waiting its turn…' : 'reading your knowledge base…')
          : run.status === 'FAILED'
            ? run.errorMessage ?? 'failed'
            : run.result?.skippedDuplicate
              ? 'already ingested'
              : `+${run.nodesCreated} new · ${run.nodesUpdated} amended`}
      </span>
    </div>
  );
}

/** Where someone lands after the first pages exist, so the panel has somewhere to point. */
export function StarterFollowUp({ projectSlug }: { projectSlug: string }) {
  return (
    <Link to={`/p/${projectSlug}/kb`} className="text-[11px] text-orange-500 hover:text-orange-600">
      Open the knowledge base
    </Link>
  );
}
